import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X, Camera, FileText, Loader2, ScanLine } from 'lucide-react';
import { api } from '../lib/api';
import { useToast } from '../components/Toast';
import { useMembers } from '../lib/members';
import { takePendingScan, dataUrlToFile } from '../lib/scanner';
import type { DocumentType } from '@shared/types';

type Mode = 'documento' | 'garanzia';

interface Staged {
  file_key: string;
  filename: string;
  mime: string;
  size_bytes: number;
}

export function NuovoScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { show } = useToast();
  const { activeId } = useMembers();
  const [mode, setMode] = useState<Mode>(params.get('tab') === 'garanzia' ? 'garanzia' : 'documento');
  const [saving, setSaving] = useState(false);

  // Upload file (fronte documento / scontrino garanzia)
  const fileInput = useRef<HTMLInputElement>(null);
  const [staged, setStaged] = useState<Staged | null>(null);
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const res = await api.uploadFile(file);
      setStaged({ file_key: res.file_key, filename: res.filename, mime: res.mime, size_bytes: res.size_bytes });
      show('File caricato');
    } catch {
      show('Upload non riuscito (richiede login)');
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permette di ricaricare lo stesso file
    if (file) await uploadFile(file);
  };

  // Immagine acquisita dalla Scansione guidata (§6.16) → carica come allegato.
  useEffect(() => {
    const scan = takePendingScan();
    if (scan) void uploadFile(dataUrlToFile(scan.dataUrl, scan.filename));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Campi documento
  const [docType, setDocType] = useState<DocumentType>('cie');
  const [holder, setHolder] = useState('');
  const [number, setNumber] = useState('');
  const [expires, setExpires] = useState('');

  // Campi garanzia
  const [product, setProduct] = useState('');
  const [seller, setSeller] = useState('');
  const [amount, setAmount] = useState('');
  const [purchasedAt, setPurchasedAt] = useState('');
  const [months, setMonths] = useState('24');
  const [notes, setNotes] = useState('');

  const onSave = async () => {
    setSaving(true);
    try {
      if (mode === 'documento') {
        if (!holder) return show('Inserisci l\'intestatario');
        await api.createDocument({
          type: docType,
          title: DOC_TITLES[docType],
          holder,
          number: number || null,
          expires_at: expires || null,
          front_key: staged?.file_key ?? null,
          owner_member_id: activeId ?? undefined,
        });
      } else {
        if (!product || !purchasedAt) return show('Prodotto e data acquisto obbligatori');
        await api.createWarranty({
          product,
          seller: seller || null,
          amount_cents: amount ? Math.round(parseFloat(amount.replace(',', '.')) * 100) : null,
          purchased_at: purchasedAt,
          warranty_months: Number(months) || 24,
          notes: notes || null,
          attachments: staged
            ? [{ file_key: staged.file_key, filename: staged.filename, mime: staged.mime, size_bytes: staged.size_bytes }]
            : undefined,
          owner_member_id: activeId ?? undefined,
        });
      }
      show('Salvato nell\'archivio');
      navigate(mode === 'documento' ? '/documenti' : '/garanzie');
    } catch {
      show('Salvataggio non riuscito (richiede login)');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="screen" style={{ paddingBottom: 100 }}>
      <div className="topbar">
        <div className="topbar__title">Nuovo</div>
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Chiudi">
          <X size={20} />
        </button>
      </div>

      <div className="segmented">
        <button className={mode === 'documento' ? 'is-active' : ''} onClick={() => setMode('documento')}>
          Documento
        </button>
        <button className={mode === 'garanzia' ? 'is-active' : ''} onClick={() => setMode('garanzia')}>
          Garanzia
        </button>
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        capture="environment"
        hidden
        onChange={onPickFile}
      />
      <button type="button" className="upload" style={{ width: '100%' }} onClick={() => fileInput.current?.click()}>
        {uploading ? (
          <>
            <Loader2 size={26} className="spin" style={{ color: 'var(--accent)', marginBottom: 8 }} />
            <strong>Caricamento…</strong>
          </>
        ) : staged ? (
          <>
            <FileText size={26} style={{ color: 'var(--accent)', marginBottom: 8 }} />
            <strong>{staged.filename}</strong>
            <span>{Math.max(1, Math.round(staged.size_bytes / 1024))} KB · tocca per sostituire</span>
          </>
        ) : (
          <>
            <Camera size={26} style={{ color: 'var(--accent)', marginBottom: 8 }} />
            <strong>Scatta o carica una foto</strong>
            <span>JPG, PNG o PDF · {mode === 'documento' ? 'fronte e retro' : 'scontrino o fattura'}</span>
          </>
        )}
      </button>

      <button
        type="button"
        className="btn"
        style={{ width: '100%', marginTop: 10 }}
        onClick={() => navigate('/scansiona')}
      >
        <ScanLine size={18} /> Scansione guidata
      </button>

      {mode === 'documento' ? (
        <>
          <Field label="Tipo">
            <select className="field__select" value={docType} onChange={(e) => setDocType(e.target.value as DocumentType)}>
              <option value="cie">Carta d'identità</option>
              <option value="patente">Patente</option>
              <option value="passaporto">Passaporto</option>
              <option value="tessera_sanitaria">Tessera sanitaria</option>
              <option value="altro">Altro</option>
            </select>
          </Field>
          <Field label="Intestatario">
            <input className="field__input" value={holder} onChange={(e) => setHolder(e.target.value)} placeholder="Mario Rossi" />
          </Field>
          <div className="field-row">
            <Field label="Numero">
              <input className="field__input mono" value={number} onChange={(e) => setNumber(e.target.value)} />
            </Field>
            <Field label="Scadenza">
              <input className="field__input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
            </Field>
          </div>
        </>
      ) : (
        <>
          <Field label="Prodotto">
            <input className="field__input" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="iPhone 15" />
          </Field>
          <Field label="Venditore">
            <input className="field__input" value={seller} onChange={(e) => setSeller(e.target.value)} placeholder="MediaWorld" />
          </Field>
          <div className="field-row">
            <Field label="Importo (€)">
              <input className="field__input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="979,00" />
            </Field>
            <Field label="Data acquisto">
              <input className="field__input" type="date" value={purchasedAt} onChange={(e) => setPurchasedAt(e.target.value)} />
            </Field>
          </div>
          <Field label="Durata garanzia (mesi)">
            <input className="field__input" inputMode="numeric" value={months} onChange={(e) => setMonths(e.target.value)} />
          </Field>
          <Field label="Note">
            <input className="field__input" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </>
      )}

      <div className="actionbar">
        <button className="btn btn--accent btn--block" disabled={saving} onClick={onSave}>
          {saving ? 'Salvataggio…' : 'Salva nell\'archivio'}
        </button>
      </div>
    </div>
  );
}

const DOC_TITLES: Record<DocumentType, string> = {
  cie: "Carta d'identità",
  patente: 'Patente di guida',
  passaporto: 'Passaporto',
  tessera_sanitaria: 'Tessera sanitaria',
  altro: 'Documento',
};

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="field">
      <label className="field__label">{label}</label>
      {children}
    </div>
  );
}
